import { workflowsOutcomes } from "./workflows-outcomes";

/** Workflows phase 4 (M26): canvas, versions, reusable workflows, webhook wait, goals and the template library. Merged into `engage.workflows`. */
export const workflowsP4 = {
  triggers: { reusable: "Reusable workflow" },
  triggerHints: { reusable: "Shared steps other workflows (or staff in the Inbox) can run." },
  conditionGroups: { webhook: "Webhook" },
  columns: { engaged: "Engaged", goal: "Goal" },
  goalTitle: "Goal: {{event}}",
  tabs: { build: "Builder", overview: "Overview", paths: "Path analysis", runs: "Runs", versions: "Versions", translations: "Translations" },
  steps: { run_workflow: "Run a workflow", wait_for_webhook: "Wait for webhook" },
  summaries: {
    run_workflow: "Runs {{workflow}}",
    wait_for_webhook_one: "Wait up to {{count}} hour for your system",
    wait_for_webhook_other: "Wait up to {{count}} hours for your system",
  },
  exits: { received: "Received", timedOut: "Timed out" },
  incomplete: { workflow: "Choose the workflow to run" },
  fields: {
    reusablePlan: "Running another workflow needs the {{plan}} plan or higher.",
    reusableWorkflow: "Workflow to run",
    reusableHint: "Only reusable workflows that work behind the scenes can be run. It runs on this conversation, then this workflow carries on. Up to {{depth}} levels deep; a workflow can't run itself, directly or through others.",
    reusableNotLive: "{{name}} (not live yet)",
    reusableNotLiveWarning: "{{name}} is not live. Publish it before you publish this workflow.",
    reusableNone: "There are no reusable workflows yet. Create one with the “Reusable workflow” trigger.",
    reusableOpenList: "Open Workflows",
    reusableOpen: "Open {{name}}",
    hookTimeout: "Wait up to (hours)",
    hookTimeoutHint: "If your system hasn't called by then, the run takes the “timed out” exit. At most {{max}} hours (7 days).",
    hookExample: "Example of what your system sends",
    hookExampleHint: "A flat JSON object (up to {{keys}} fields). Each field becomes a variable starting with {{prefix}} for the steps after this one. The example is only for the builder.",
    hookExamplePlaceholder: "{\n  \"order_id\": \"A-1042\",\n  \"paid\": true\n}",
    hookExampleProblems: {
      json: "This isn't valid JSON yet.",
      object: "Use a JSON object like { \"order_id\": \"A-1042\" }.",
      count: "Use at most {{max}} fields.",
      key: "“{{key}}” isn't a valid field name. Start with a letter; use letters, digits and underscores.",
      duplicate: "“{{key}}” appears twice (field names are not case-sensitive).",
      value: "“{{key}}” must be text (up to {{chars}} characters), a number, true, false or null.",
    },
    hookVariables: "Variables from the call",
    hookReceivedGoTo: "When the call arrives, go to",
    hookReceivedHint: "With no path chosen, the run carries on with the next step.",
    hookTimeoutGoTo: "If it times out, go to",
    hookTimeoutGoToHint: "With no path chosen, a timeout ends the run.",
    nextStep: "The next step",
  },
  hook: {
    specFailed: "We couldn't load how to call the URL.",
    howTitle: "How your system calls it",
    howBody: "When a run reaches this step it gets its own secret URL. Your system sends a signed JSON request to it and the run continues.",
    urlPattern: "URL",
    copyPattern: "Copy the URL pattern",
    copied: "Copied",
    stepSecret: "Open the run (Runs tab) to see its URL and secret while it waits.",
    stepSign: "Sign the request: put a timestamp and the signature in the {{header}} header.",
    stepSend: "Send JSON up to {{limit}} KB: flat fields only, at most {{keys}} fields, {{chars}} characters per value.",
    signatureLabel: "Signature header format",
    tolerance: "Requests older than {{minutes}} minutes are refused. Send an {{header}} to make a retry safe.",
    runTitle: "Waiting for your system",
    statuses: { waiting: "Waiting", received: "Received", timed_out: "Timed out", cancelled: "Cancelled" },
    receivedAt: "Received {{when}}",
    timeoutAt: "Times out {{when}}",
    runUrl: "URL for this run",
    copyUrl: "Copy the URL",
    runSecret: "Secret for this run",
    showSecret: "Show the secret",
    hideSecret: "Hide the secret",
    copySecret: "Copy the secret",
    runSecretHint: "Keep the secret on your server. Anyone who has it can continue this run.",
    closed: "The wait is over, so the URL and secret are no longer shown.",
    managersOnly: "Only members who can manage workflows see the URL and secret.",
  },
  goal: {
    label: "Goal (optional)",
    hint: "What counts as success for this workflow. It shows as Goal % on the list and Overview.",
    none: "No goal",
    events: {
      booking_confirmed: "Booking confirmed",
      lead_created: "Lead captured",
      conversation_resolved: "Conversation resolved",
      conversation_rated: "Conversation rated",
    },
    within: "Counted when it happens within",
    withinHint: "From the moment the run starts. Between 1 hour and {{max}} hours (30 days).",
    hours: "hours",
    window_hours_one: "{{count}} hour",
    window_hours_other: "{{count}} hours",
    window_days_one: "{{count}} day",
    window_days_other: "{{count}} days",
  },
  canvas: {
    view: "View",
    list: "List",
    canvas: "Canvas",
    title: "Canvas view",
    toolbar: "Canvas controls",
    region: "Workflow canvas",
    hint: "Canvas view of the same workflow. Use the List view with a keyboard or screen reader: both edit the same steps. Every exit has a dot: drag it onto a path to connect it, or press Enter on it for a menu. On the canvas, drag the background to pan, use the arrow keys, and zoom with + and -, the buttons, or Ctrl + scroll.",
    zoomIn: "Zoom in",
    zoomOut: "Zoom out",
    resetZoom: "Reset zoom",
    tidyUp: "Tidy up",
    agents_one: "{{count}} agent",
    agents_other: "{{count}} agents",
    triggerSheetHint: "What starts this workflow, and who it is for.",
  },
  versions: {
    title: "Versions",
    subtitle: "Every publish saves a version. Rolling back creates a new draft from it: runs in progress finish on their own version.",
    draft: "Draft",
    live: "Live",
    versionLive: "Version {{number}} (live)",
    versionName: "Version {{number}}",
    versionShort: "v{{number}}",
    rowMeta: "{{name}} · {{when}} · {{runs}} runs",
    draftMeta: "{{name}} · {{when}}",
    view: "View",
    viewVersion: "View version {{number}}",
    compareLive: "Compare with live",
    compareVersion: "Compare version {{number}} with live",
    compareWithLive: "Compare with live",
    rollback: "Roll back",
    rollbackVersion: "Roll back to version {{number}}",
    rolledBack: "Version {{number}} is now your draft. Publish it to make it live.",
    loadFailed: "We couldn't load the versions.",
    hidden_one: "Your plan shows the last {{limit}} versions. {{count}} older version is hidden.",
    hidden_other: "Your plan shows the last {{limit}} versions. {{count}} older versions are hidden.",
    upgrade: "See plans",
    emptyTitle: "No versions yet",
    emptyBody: "Publish the workflow to save version 1.",
    viewTitle: "Version {{number}}",
    viewMeta: "Published {{when}} by {{name}}",
    readOnly: "This is a published version. You can look, but not change it.",
    readOnlyBadge: "Read-only",
    viewFailed: "We couldn't load this version.",
    forceTitle: "Replace your draft with version {{number}}?",
    forceBody: "Your draft has changes that were never published. Rolling back replaces them with version {{number}}.",
    forceBodyUnsaved: "You have changes in the builder that are not published. Rolling back replaces them with version {{number}}.",
    forceConfirm: "Replace the draft",
    compareTitle: "Compare versions",
    compareHint: "Steps added, removed, changed or moved are marked on each side.",
    compareBase: "From",
    compareTarget: "To",
    unsavedNote: "Changes you haven't saved yet are not in this comparison. Save the draft first to include them.",
    compareFailed: "We couldn't compare these versions.",
    summary: "What changed",
    counts: {
      added_one: "{{count}} step added",
      added_other: "{{count}} steps added",
      removed_one: "{{count}} step removed",
      removed_other: "{{count}} steps removed",
      changed_one: "{{count}} step changed",
      changed_other: "{{count}} steps changed",
      moved_one: "{{count}} step moved",
      moved_other: "{{count}} steps moved",
    },
    settingsChanged: "Settings changed",
    translationsChanged: "Translations changed",
    identicalTitle: "These are the same",
    identicalBody: "No step, setting or translation differs.",
    status: { added: "Added", removed: "Removed", changed: "Changed", moved: "Moved", unchanged: "Same" },
    changedFields: "Changed: {{fields}}",
    noSteps: "No steps",
  },
  analysis: {
    title: "Path analysis",
    subtitle: "Where visitors continue and where they drop off, step by step.",
    version: "Version",
    allVersions: "All versions",
    versionLive: "v{{number}} (live)",
    versionName: "v{{number}}",
    period: "Period",
    days_one: "Last {{count}} day",
    days_other: "Last {{count}} days",
    loadFailed: "We couldn't load the path analysis.",
    emptyTitle: "No runs to analyse",
    emptyBody: "Once visitors go through this version, you'll see how far each step gets.",
    sent_one: "{{formatted}} run started",
    sent_other: "{{formatted}} runs started",
    columns: { step: "Path · step", reached: "Reached", continued: "Continued", dropped: "Dropped off", waiting: "Waiting", failed: "Failed", filled: "Data filled" },
    answersTitle: "Which button visitors pick",
    answerCount: "{{count}} · {{share}}",
    continuedOf: "Continued from {{step}}",
    note: "A visitor can be counted on more than one path. Preview runs are excluded.",
    ...workflowsOutcomes.analysis,
  },
  translations: {
    title: "Translations",
    subtitle: "Visitors see the version in their language. Collected answers are never translated.",
    primary: "{{language}} · primary",
    languages: { en: "English", fil: "Filipino" },
    complete: "Complete",
    missing_one: "{{count}} string to review",
    missing_other: "{{count}} strings to review",
    emptyTitle: "Nothing to translate yet",
    emptyBody: "Messages, reply buttons, questions and rating prompts show up here once you've written them.",
    savedWithDraft: "Translations are saved with the draft: use Save draft or Publish.",
    translationOf: "{{language}} translation of: {{text}}",
    kinds: { text: "Message", button: "Button", field_label: "Field label" },
    fallback: "Shows the English text",
    fallbackBuiltIn: "Shows the built-in wording (“Start over” or “Magsimula ulit”)",
    aiDraft: "AI draft: review it",
    draftWithAi: "Auto-translate missing strings",
    draftNothing: "Nothing to draft: every text already has a translation.",
    drafted_one: "Draft translations created — review before publishing",
    drafted_other: "Draft translations created — review before publishing",
    stepTitle: "Path {{path}} · {{id}}",
  },
  runs: {
    cancel: "Cancel run",
    cancelTitle: "Cancel this run?",
    cancelBody: "The run stops where it is and its remaining steps don't happen. The visitor is not told.",
    keepRunning: "Keep it running",
    cancelConfirm: "Cancel the run",
    cancelled: "Run cancelled",
    alreadyEnded: "That run had already ended.",
    calledBy: "Started by another workflow (level {{depth}}).",
    goalMet: "Goal met {{when}}",
    goalReached: "Goal met",
    endedReasons: { cancelled_by_staff: "A team member cancelled it.", timed_out: "Your system did not call in time." },
  },
  overview: {
    engaged: "Engaged",
    engagedHint: "{{formatted}} tapped or answered",
    goal: "Goal",
    goalHint: "{{met}} of {{tracked}} runs: {{event}} within {{window}}",
    goalNone: "Set a goal in the trigger settings",
  },
  inbox: {
    button: "Run workflow",
    label: "Run a reusable workflow",
    loading: "Loading workflows…",
    empty: "No live reusable workflows yet. Create one in Workflows.",
    active: "Running now",
    stop: "Stop {{name}}",
    started: "Started {{name}}",
    stopped: "Stopped {{name}}",
    refused: {
      not_live: "That workflow isn't live.",
      run_active: "A customer-facing workflow is already running on this conversation.",
      person_has_conversation: "A person has this conversation. Hand it back first.",
      switched_off: "Workflows are switched off for this workspace.",
    },
  },
  templates: {
    open: "Templates",
    loadFailed: "We couldn't load the templates.",
    requirements: {
      setupTitle: "After you create it",
      title: "This template uses",
      hint: "It points at these in your workspace. You can change them in the builder later.",
      connectorLabel: "Records system endpoint (Only from my workflows)",
      connectorMissing: "This template asks your own system a question, but you have no webhook endpoint set to “Only from my workflows” yet.",
      labels: { booking_action: "Booking action", endpoint: "Webhook endpoint", team: "Team", user: "Person to notify", lead_stage: "Lead stage" },
      missing: {
        booking_action: "This template shows a booking card, but you have no booking action yet.",
        endpoint: "This template sends a webhook, but you have no webhook endpoint yet.",
        team: "This template needs a team, but you have none yet.",
        user: "This template needs a person to notify, but none could be found.",
        lead_stage: "This template moves a lead, but you have no lead stages.",
      },
      create: { booking_action: "Create a booking action", endpoint: "Add a webhook endpoint", team: "Create a team", user: "Open Team", lead_stage: "Open Leads" },
    },
  },
} as const;
